<template>
    <div
        id="sensoralertcard"
        :class="[$mq, cardClass, { interactive: isInteractive }]"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @click="handleClick"
    >
        <div class="top-card">
            <div class="left-top-card" :class="cardClass">
                {{ formatReading(sensor.data.ref.value) }}ºC
            </div>
            <div class="right-top-card">
                <div class="issue-id" v-if="sensor.issue_id && cardClass === 'open-issue-card'">
                    {{ '#' + sensor.issue_id }}
                </div>
            </div>
        </div>
        <div class="bottom-card" :class="cardClass">
            <div class="date-sensor">{{ formatDate(displayTimestamp) }}</div>
            <div class="issue-link" v-if="sensor.issue_id && cardClass === 'open-issue-card'" @click.stop="goToIssue()">
                {{ $t('sensor.issue_link') }}
                <div class="i-andy-right-arrow i-andy-s"></div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatDecimal } from '@/utils/numberFormat'

export default {
    name: 'SensorAlertCard',
    props: {
        sensor: {
            type: Object,
            default: false
        },
        sensorName: {
            type: [String, Boolean],
            default: false
        },
        cardClass: {
            type: [String, Boolean],
            default: false
        },
        interactive: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            val: 'value'
        }
    },
    methods: {
        formatReading(value) {
            const formatted = formatDecimal(value, this.$i18n.locale)
            return formatted === '' ? '-' : formatted
        },
        handleMouseEnter() {
            if (!this.isInteractive) {
                return
            }

            this.$emit('alert-hover', this.sensor)
        },
        handleMouseLeave() {
            if (!this.isInteractive) {
                return
            }

            this.$emit('alert-leave') 
        },
        handleClick() {
            if (!this.isInteractive) {
                return
            }

            this.$emit('alert-select', this.sensor)
        },
        goToIssue() {
            let id = typeof this.sensor.data.ref != 'undefined' && typeof this.sensor.data.ref.issue != 'undefined' && typeof this.sensor.data.ref.issue.id != 'undefined' ? this.sensor.data.ref.issue.id : this.sensor.id
            this.$router.push({ path: '/issues/' + id, params: { id: id } })
        },
        formatDate(date) {
            const format = 'DD/MM/YYYY HH:mm:ss'
            if (typeof date === 'undefined' || date === null) {
                return ''
            }
            if (typeof date === 'number') {
                return moment(date).format(format)
            }
            if (typeof date === 'string' && /^\d+$/.test(date)) {
                const numeric = Number(date)
                return numeric < 1e12 ? moment.unix(numeric).format(format) : moment(numeric).format(format)
            }
            const parsed = moment(date, 'YYYY-MM-DD HH:mm:ss', true)
            if (parsed.isValid()) {
                return parsed.format(format)
            }
            return moment(date).format(format)
        }
    },
    computed: {
        isInteractive() {
            return this.interactive === true
        },
        displayTimestamp() {
            if (!this.sensor) {
                return null
            }

            if (this.sensor.timestamp) {
                return this.sensor.timestamp * 1000
            }

            if (this.sensor.data && this.sensor.data.ref && this.sensor.data.ref.date) {
                return this.sensor.data.ref.date
            }

            return null
        },
        issueStates() {
            return this.$store.getters['getIssueStates']
        },
        getSensorName() {
            if (this.sensorName) {
                return this.$store.getters['sensor/getList'](this.sensorName)
            }

            return false
        }
    }
}
</script>

<style lang="scss">
#sensoralertcard {
    @include display-flex();
    @include flex-direction(column);
    @include border-radius(4px);
    width: 100%;
    height: fit-content;
    background-color: #fff;
    border: 1px solid $neutro-dark;

    &.interactive {
        cursor: pointer;
    }

    // media and max-width
    @media (max-width: 600px) {
        width: calc(100%);
    }

    @media (min-width: 601px) {
        width: calc(100% - 6px);
    }

    @media (min-width: 1200px) {
        width: calc(100% - 6px);
    }

    @media (min-width: 1600px) {
        width: calc(100% - 9px);
    }

    .top-card {
        @include display-flex();
        @include justify-content(space-between);
        @include align-items(center);
        padding: 16px 16px 8px 16px;

        .left-top-card {
            font-size: 24px;
            font-family: $text-bold;
            padding-top: 4px;
            padding-bottom: 4px;

            &.alert-card {
                color: $neutro-text;
            }
            &.closed-issue-card {
                color: $error-dark;
            }
            &.open-issue-card {
                color: $error-dark;
            }
        }
        .right-top-card {
            @include border-radius(4px);
           background-color: $neutro-t40;

           .issue-id {
            font-size: 12px;
            color: $inactive-s80;
            padding: 4px 8px;
           }
        }
    }
    .bottom-card {
        @include display-flex();
        @include justify-content(space-between);
        @include align-items(center);
        @include border-radius(4px);
        margin: 0px 16px 16px 16px;

        &.alert-card {
            background-color: #FFFAE8;
        }
        &.closed-issue-card {
            background-color: $error-t90;
        }
        &.open-issue-card {
            background-color: $error-t90;
        }

        .date-sensor {
            font-size: 14px;
            padding: 4px;
        }
        .issue-link {
            @include display-flex();
            @include align-items(center);
            @include justify-content(flex-start);
            font-size: 12px;
            color: $error-dark;
            margin-right: 4px;
            gap: 4px;
            cursor: pointer;

            .i-andy-right-arrow {
                color: $error-dark;
            }
        }
    }

    &.portrait {
        .top-card {
            .left-top-card {
                @include font-size(s);
            }
            .sensor-name {
                @include font-size(s);
            }
        }
        .bottom-card {
            .issue-id {
                @include font-size(s);
            }
            .issue-name {
                @include font-size(s);
            }
        }
    }
}
</style>
